import React, { useState, useEffect } from "react";
import { useGantiProduksi } from "./useGantiProduk";
import { bulan } from "../../util/constant";
import moment from "moment";
import "./GantiProduk.css";
import Select from "react-select";

/* ------------------------------------------------------------------ */
/* Helper                                                              */
/* ------------------------------------------------------------------ */

const formatDot = (value) =>
  value && value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".");

const numberWithCommas = (data) =>
  data &&
  data.toLocaleString("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });

const switchDotsAndCommas = (s) => {
  function switcher(match) {
    return match === "," ? "." : ",";
  }
  return s && s.replaceAll(/\.|\,/g, switcher);
};

const formatMenit = (value) =>
  formatDot(switchDotsAndCommas(numberWithCommas(value)));

const firstName = (name) => (name || "").split(" ").slice(0, 1).join(" ");

const initials = (name) =>
  (name || "")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join("") || "?";

const avatarClass = (name) => {
  let s = 0;
  for (const c of name || "") s += c.charCodeAt(0);
  return `gp_av_${s % 5}`;
};

const fmtDateTime = (t) => (t ? moment(t).format("DD-MM-YY HH:mm") : "-");

// Selesai = downtime sudah dihitung (calculate >= 0)
const isDone = (d) =>
  !!d && d.calculate !== undefined && d.calculate !== null && d.calculate >= 0;

const bagianOf = (d) => (d && d.qrMold ? "Mold" : "Produksi");

const menitOf = (d) => (d.calculate / 60).toFixed(2);

// Susun kolom board sesuai pengelompokan yang dipilih
const buildColumns = (items, groupBy, listJenis) => {
  if (groupBy === "bagian") {
    return [
      {
        key: "Mold",
        title: "Mold",
        accent: "gp_accent_mold",
        items: items.filter((d) => bagianOf(d) === "Mold"),
      },
      {
        key: "Produksi",
        title: "Produksi",
        accent: "gp_accent_produksi",
        items: items.filter((d) => bagianOf(d) === "Produksi"),
      },
    ];
  }
  if (groupBy === "jenis") {
    return (listJenis || []).map((j) => ({
      key: j,
      title: j,
      accent: "gp_accent_neutral",
      items: items.filter((d) => d.jenis === j),
    }));
  }
  return [
    {
      key: "progress",
      title: "Belum Selesai",
      accent: "gp_accent_progress",
      items: items.filter((d) => !isDone(d)),
    },
    {
      key: "done",
      title: "Selesai",
      accent: "gp_accent_done",
      items: items.filter((d) => isDone(d)),
    },
  ];
};

// Samakan tampilan react-select dengan input lain
const selectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: 40,
    borderRadius: 8,
    borderWidth: 1.5,
    borderColor: state.isFocused ? "#2d357a" : "#dcdff2",
    boxShadow: state.isFocused ? "0 0 0 3px rgba(45, 53, 122, 0.14)" : "none",
    "&:hover": { borderColor: "#2d357a" },
  }),
  menu: (base) => ({ ...base, zIndex: 20 }),
};

/* ------------------------------------------------------------------ */
/* Komponen kecil                                                      */
/* ------------------------------------------------------------------ */

const FilterItem = ({ label, wide, children }) => (
  <div className={wide ? "gp_filter_item gp_filter_wide" : "gp_filter_item"}>
    <p className="gp_label">{label}</p>
    {children}
  </div>
);

const Segmented = ({ options, value, onChange }) => (
  <div className="gp_seg">
    {options.map((opt) => (
      <button
        key={opt.value}
        type="button"
        className={
          value === opt.value ? "gp_seg_btn gp_seg_btn_active" : "gp_seg_btn"
        }
        onClick={() => onChange(opt.value)}
      >
        {opt.label}
      </button>
    ))}
  </div>
);

const BagianChip = ({ data }) => (
  <span
    className={
      bagianOf(data) === "Mold"
        ? "gp_chip gp_chip_mold"
        : "gp_chip gp_chip_produksi"
    }
  >
    {bagianOf(data).toUpperCase()}
  </span>
);

// Sel approval di tabel: nama + tanggal - jam
const ApprovalCell = ({ name, time }) =>
  time ? (
    <>
      <div className="gp_approval_name">{firstName(name)}</div>
      <div className="gp_approval_time">
        {moment(time).format("DD-MM-YY")}&nbsp;<b>&ndash;</b>&nbsp;
        {moment(time).format("HH:mm:ss")}
      </div>
    </>
  ) : (
    ""
  );

/* Kartu di board */
const GantiCard = ({ data, onOpen }) => {
  const done = isDone(data);
  return (
    <div
      className={
        bagianOf(data) === "Mold" ? "gp_card" : "gp_card gp_card_produksi"
      }
      role="button"
      tabIndex={0}
      onClick={() => onOpen(data)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onOpen(data);
        }
      }}
    >
      <p className="gp_card_title">{data.moldProduk}</p>
      <p className="gp_card_sub">
        {[data.customer, data.color].filter(Boolean).join(" · ")}
      </p>

      <div className="gp_card_tags">
        <BagianChip data={data} />
        {data.jenis && <span className="gp_tag">{data.jenis}</span>}
      </div>

      <div className="gp_card_foot">
        <span className="gp_card_key">Mc {data.mesin}</span>
        <span
          className={
            done ? "gp_card_time gp_time_done" : "gp_card_time gp_time_progress"
          }
        >
          {done ? `${menitOf(data)} menit` : "Belum selesai"}
        </span>
        <span
          className={`gp_avatar ${avatarClass(data.approverStart)}`}
          title={data.approverStart || ""}
        >
          {initials(data.approverStart)}
        </span>
      </div>

      {data.startTime && (
        <p className="gp_card_date">Mulai {fmtDateTime(data.startTime)}</p>
      )}
    </div>
  );
};

/* Kolom di board */
const BoardColumn = ({ column, onOpen }) => {
  const totalMenit = column.items.reduce(
    (s, d) => (d.calculate ? s + Number(d.calculate) / 60 : s),
    0
  );
  return (
    <section className="gp_column">
      <div className="gp_column_head">
        <span className={`gp_dot ${column.accent}`} />
        <h3 className="gp_column_title">{column.title}</h3>
        <span className="gp_count">{column.items.length}</span>
        {totalMenit > 0 && (
          <span className="gp_column_total">{formatMenit(totalMenit)} mnt</span>
        )}
      </div>
      <div className="gp_column_body">
        {column.items.length === 0 && (
          <p className="gp_column_empty">Tidak ada kartu</p>
        )}
        {column.items.map((data, i) => (
          <GantiCard key={data.id || i} data={data} onOpen={onOpen} />
        ))}
      </div>
    </section>
  );
};

/* Panel detail (muncul dari kanan) */
const DetailDrawer = ({ data, onClose }) => {
  const done = isDone(data);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div className="gp_overlay" onClick={onClose}>
      <aside
        className="gp_drawer"
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="gp_drawer_head">
          <span className="gp_drawer_key">
            Mc {data.mesin} · {bagianOf(data).toUpperCase()}
          </span>
          <button
            type="button"
            className="gp_close"
            onClick={onClose}
            aria-label="Tutup"
          >
            &times;
          </button>
        </div>

        <div className="gp_drawer_body">
          <h2 className="gp_drawer_title">{data.moldProduk}</h2>
          <p className="gp_drawer_sub">
            {[data.customer, data.color].filter(Boolean).join(" · ")}
          </p>
          <div className="gp_drawer_chips">
            <span
              className={
                done ? "gp_chip gp_chip_done" : "gp_chip gp_chip_pending"
              }
            >
              {done ? "SELESAI" : "BELUM SELESAI"}
            </span>
            <BagianChip data={data} />
            {data.jenis && <span className="gp_tag">{data.jenis}</span>}
          </div>

          <p className="gp_section">Detail</p>
          <dl className="gp_detail">
            <dt>Customer</dt>
            <dd>{data.customer || "-"}</dd>
            <dt>Warna</dt>
            <dd>{data.color || "-"}</dd>
            <dt>Mesin</dt>
            <dd>{data.mesin}</dd>
            <dt>Bagian</dt>
            <dd>{bagianOf(data)}</dd>
            <dt>Jenis Ganti</dt>
            <dd>{data.jenis || "-"}</dd>
            <dt>Downtime</dt>
            <dd>{done ? `${menitOf(data)} menit` : "Belum selesai"}</dd>
          </dl>

          <p className="gp_section">Riwayat Approval</p>
          <ul className="gp_timeline">
            <li className="gp_step">
              <p className="gp_step_title">Mulai</p>
              <p className="gp_step_who">{data.approverStart || "-"}</p>
              <p className="gp_step_time">{fmtDateTime(data.startTime)}</p>
            </li>
            {data.endTime ? (
              <li className="gp_step gp_step_end">
                <p className="gp_step_title">Selesai</p>
                <p className="gp_step_who">{data.approver || "-"}</p>
                <p className="gp_step_time">{fmtDateTime(data.endTime)}</p>
              </li>
            ) : (
              <li className="gp_step gp_step_pending">
                <p className="gp_step_title">Selesai</p>
                <p className="gp_step_time">Belum ada approval selesai</p>
              </li>
            )}
          </ul>

          <p className="gp_section">Keterangan</p>
          {data.keterangan ? (
            <p className="gp_note">{data.keterangan}</p>
          ) : (
            <p className="gp_note gp_note_empty">Tidak ada keterangan</p>
          )}
        </div>
      </aside>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* Halaman                                                             */
/* ------------------------------------------------------------------ */

const GantiProduk = () => {
  const {
    filterYear,
    setFilterYear,
    filterMonth,
    setFilterMonth,
    dataTemp,
    listSelectProduk,
    produkPick,
    setProdukPick,
    handleReset,
    bagianPick,
    setBagianPick,
    listJenis,
    jenisPick,
    setJenisPick,
  } = useGantiProduksi();

  const [view, setView] = useState("board"); // "board" | "table"
  const [groupBy, setGroupBy] = useState("status"); // status | bagian | jenis
  const [selected, setSelected] = useState(null);

  // Sort in-place (sama seperti sebelumnya): terbaru di atas
  const sortedArray =
    dataTemp &&
    dataTemp.sort((a, b) => new Date(b.startTime) - new Date(a.startTime));

  const items = sortedArray || [];

  const sum = items.reduce(function (s, a) {
    if (a.calculate) {
      return s + Number(a.calculate);
    }
    return s;
  }, 0);

  const count = items.length;
  const totalMenit = sum ? sum / 60 : 0;
  const rataMenit = count ? totalMenit / count : 0;

  const columns = buildColumns(items, groupBy, listJenis);

  return (
    <div className="container_list_bahan">
      <p className="gp_title">Downtime Ganti Produksi</p>

      {/* ===================== FILTER ===================== */}
      <div className="gp_filter_bar">
        <FilterItem label="Bulan">
          <select
            className="gp_select"
            value={filterMonth}
            onChange={(e) => setFilterMonth(e.target.value)}
          >
            <option value="Pilih Bulan" disabled>
              Pilih Bulan
            </option>
            {bulan &&
              bulan.map((data) => (
                <option key={data.value} value={data.value}>
                  {data.name}
                </option>
              ))}
          </select>
        </FilterItem>

        <FilterItem label="Tahun">
          <select
            className="gp_select gp_select_year"
            value={filterYear}
            onChange={(e) => setFilterYear(e.target.value)}
          >
            <option value="Pilih Tahun" disabled>
              Pilih Tahun
            </option>
            <option value="2025">2025</option>
            <option value="2026">2026</option>
          </select>
        </FilterItem>

        <FilterItem label="Bagian">
          <select
            className="gp_select"
            value={bagianPick || "Pilih Bagian"}
            onChange={(e) => setBagianPick(e.target.value)}
          >
            <option value="Pilih Bagian" disabled>
              Pilih Bagian
            </option>
            <option value="Mold">Mold</option>
            <option value="Produksi">Produksi</option>
          </select>
        </FilterItem>

        <FilterItem label="Produk" wide>
          <Select
            options={listSelectProduk}
            value={produkPick || ""}
            onChange={setProdukPick}
            styles={selectStyles}
          />
        </FilterItem>

        <FilterItem label="Jenis">
          <select
            className="gp_select"
            value={jenisPick}
            onChange={(e) => setJenisPick(e.target.value)}
          >
            <option value="Pilih Jenis" disabled>
              Pilih Jenis
            </option>
            {listJenis &&
              listJenis.map((data) => (
                <option key={data} value={data}>
                  {data}
                </option>
              ))}
          </select>
        </FilterItem>

        <button
          type="button"
          className="gp_btn_ghost"
          onClick={() => handleReset()}
        >
          Reset
        </button>
      </div>

      {/* ===================== RINGKASAN ===================== */}
      <div className="gp_summary">
        <div className="gp_stat">
          <p className="gp_stat_label">Total Downtime</p>
          <p className="gp_stat_value">
            {formatMenit(totalMenit)}
            <span className="gp_stat_unit">Menit</span>
          </p>
        </div>
        <div className="gp_stat gp_stat_avg">
          <p className="gp_stat_label">Rata-rata</p>
          <p className="gp_stat_value">
            {formatMenit(rataMenit)}
            <span className="gp_stat_unit">Menit</span>
          </p>
        </div>
      </div>

      {/* ===================== TOOLBAR TAMPILAN ===================== */}
      <div className="gp_toolbar">
        <Segmented
          value={view}
          onChange={setView}
          options={[
            { value: "board", label: "Board" },
            { value: "table", label: "Tabel" },
          ]}
        />
        {view === "board" && (
          <div className="gp_group">
            <span className="gp_group_label">Kelompokkan:</span>
            <Segmented
              value={groupBy}
              onChange={setGroupBy}
              options={[
                { value: "status", label: "Status" },
                { value: "bagian", label: "Bagian" },
                { value: "jenis", label: "Jenis" },
              ]}
            />
          </div>
        )}
      </div>

      {/* ===================== BOARD ===================== */}
      {view === "board" && (
        <div className="gp_board">
          {columns.map((column) => (
            <BoardColumn
              key={column.key}
              column={column}
              onOpen={setSelected}
            />
          ))}
        </div>
      )}

      {/* ===================== TABEL ===================== */}
      {view === "table" && (
        <div className="gp_table_wrap">
          <table className="gp_table">
            <thead>
              <tr>
                <th>Mc</th>
                <th>Produk</th>
                <th>Bagian</th>
                <th>Downtime (Menit)</th>
                <th>Approval Start</th>
                <th>Approval End</th>
                <th>Ganti</th>
                <th>Keterangan</th>
              </tr>
            </thead>
            <tbody>
              {sortedArray && sortedArray.length === 0 && (
                <tr>
                  <td className="gp_empty" colSpan={8}>
                    Tidak ada data
                  </td>
                </tr>
              )}
              {items.map((data, index) => (
                <tr key={data.id || index} onClick={() => setSelected(data)}>
                  <td className="gp_td_mesin">{data.mesin}.</td>
                  <td className="gp_td_text">{data.moldProduk}</td>
                  <td>
                    <BagianChip data={data} />
                  </td>
                  <td>
                    {isDone(data) ? (
                      <span className="gp_downtime">{menitOf(data)}</span>
                    ) : (
                      <span className="gp_chip gp_chip_pending">
                        Belum Selesai
                      </span>
                    )}
                  </td>
                  <td>
                    <ApprovalCell
                      name={data.approverStart}
                      time={data.startTime}
                    />
                  </td>
                  <td>
                    <ApprovalCell name={data.approver} time={data.endTime} />
                  </td>
                  <td>{data.jenis}</td>
                  <td className="gp_td_ket">{data.keterangan}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ===================== DETAIL ===================== */}
      {selected && (
        <DetailDrawer data={selected} onClose={() => setSelected(null)} />
      )}
      <br />
    </div>
  );
};

export default GantiProduk;
